import React from 'react';
import { AlertTriangle, CheckCircle2, MinusCircle } from 'lucide-react';
import type { FitHolding, PortfolioFitResponse } from '../../services/portfolioFitService';
import { OpenAsSpreadsheet } from '../canvas/widgets/OpenAsSpreadsheet';

interface FitHeatmapProps {
  fit: PortfolioFitResponse | null | undefined;
  loading?: boolean;
}

const formatPercent = (value: number | null | undefined) => (
  value === null || value === undefined ? 'n/a' : `${Math.round(value * 100)}%`
);

const scoreTone = (score: number | null) => {
  if (score === null) return 'bg-gray-100 text-gray-600';
  if (score === 0) return 'bg-red-50 text-red-700 border-red-200';
  if (score >= 0.75) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  return 'bg-amber-50 text-amber-700 border-amber-200';
};

const conflictText = (holding: FitHolding) => {
  const conflicts = holding.conflicts || [];
  if (!conflicts.length) return 'Clear';
  return conflicts.map((c) => c.tag).join(', ');
};

const FitHeatmap: React.FC<FitHeatmapProps> = ({ fit, loading = false }) => {
  if (loading) {
    return (
      <section className="bg-white rounded-lg shadow p-5">
        <div className="h-5 w-36 bg-gray-200 rounded animate-pulse" />
        <div className="mt-5 space-y-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />)}
        </div>
      </section>
    );
  }

  const holdings = fit?.holdings || [];
  const conflicted = holdings.filter((h) => h.fit_score === 0).length;

  return (
    <section className="bg-white rounded-lg shadow p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#002B45]">Fit Heatmap</h2>
          <p className="text-sm text-gray-600 mt-1">
            {fit?.empty_reason === 'no_positions_found'
              ? 'No positions found'
              : `${fit?.scored_holdings || 0} of ${fit?.total_holdings || 0} holdings scored`}
          </p>
          {/* TASK-876: the conflicts table is a handover artefact — "these eleven
              holdings breach the mandate, check row 4" — and had no route out of the
              screen. `position_id` leads the sheet so a row can be referred to. */}
          {holdings.length > 0 && (
            <div className="mt-2">
              <OpenAsSpreadsheet
                sourceWidget="FitHeatmap"
                clientId={fit?.client_id}
                build={() => ({
                  title: 'Fit heatmap',
                  sheetName: 'Fit',
                  columns: [
                    { header: 'Position id', width: 38 },
                    { header: 'Holding', width: 32 },
                    { header: 'ISIN', width: 16 },
                    { header: 'Fit', fmt: 'percent', width: 10 },
                    { header: 'DNA signal', width: 32 },
                  ],
                  rows: holdings.map((h) => [
                    h.position_id ?? '',
                    h.security_name || 'Unnamed holding',
                    h.isin || '',
                    h.fit_score,
                    conflictText(h),
                  ]),
                })}
              />
            </div>
          )}
        </div>
        <div className="grid grid-cols-3 gap-3 text-right">
          <div>
            <div className="text-xl font-semibold text-[#002B45]">{formatPercent(fit?.portfolio_fit)}</div>
            <div className="text-xs text-gray-500">Fit</div>
          </div>
          <div>
            <div className="text-xl font-semibold text-[#002B45]">{formatPercent(fit?.coverage)}</div>
            <div className="text-xs text-gray-500">Coverage</div>
          </div>
          <div>
            <div className="text-xl font-semibold text-red-700">{conflicted}</div>
            <div className="text-xs text-gray-500">Conflicts</div>
          </div>
        </div>
      </div>

      {holdings.length === 0 ? (
        <div className="mt-6 flex items-center gap-2 text-sm text-gray-600">
          <MinusCircle className="h-4 w-4" />
          <span>No fit rows are available for this client.</span>
        </div>
      ) : (
        <div className="mt-5 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="py-2 pr-4 font-medium">Holding</th>
                <th className="py-2 pr-4 font-medium">ISIN</th>
                <th className="py-2 pr-4 font-medium">Fit</th>
                <th className="py-2 pr-4 font-medium">DNA signal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {holdings.map((holding) => {
                const hasConflict = holding.fit_score === 0;
                return (
                  <tr key={holding.position_id}>
                    <td className="py-3 pr-4 text-gray-900">{holding.security_name || 'Unnamed holding'}</td>
                    <td className="py-3 pr-4 text-gray-600">{holding.isin || 'n/a'}</td>
                    <td className="py-3 pr-4">
                      <span className={`inline-flex min-w-[64px] items-center justify-center rounded border px-2 py-1 text-xs font-medium ${scoreTone(holding.fit_score)}`}>
                        {formatPercent(holding.fit_score)}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <span className={`inline-flex items-center gap-1 ${hasConflict ? 'text-red-700' : 'text-emerald-700'}`}>
                        {hasConflict ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                        {conflictText(holding)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default FitHeatmap;
